/**
 * Client-side mirror of the Cognito user-pool password policy.
 *
 * The authoritative check is server-side (Cognito rejects non-conforming
 * passwords on `confirmResetPassword`/`confirmSignIn` with an
 * InvalidPasswordException). This helper exists so the UI can give the user
 * immediate, friendly feedback before the round-trip. Keep it in sync with the
 * `password_policy` block in `terraform/cognito.tf`:
 *   minimum_length = 8, require_lowercase/uppercase/numbers/symbols = true.
 */

export const PASSWORD_MIN_LENGTH = 8;

/** Human-readable list of the rules, suitable for rendering as a checklist. */
export const PASSWORD_RULES: string[] = [
  `Be at least ${PASSWORD_MIN_LENGTH} characters long`,
  'Contain uppercase and lowercase letters',
  'Contain at least one number',
  'Contain at least one special character',
];

/**
 * Returns a user-facing error string when `password` violates the policy, or
 * `null` when it is acceptable. A single, specific message is returned (the
 * first rule that fails) so the user is not overwhelmed.
 */
export function getPasswordPolicyError(password: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters long`;
  }
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  if (!/[0-9]/.test(password)) {
    return 'Password must contain at least one number';
  }
  // Cognito treats any non-alphanumeric printable character as a symbol.
  if (!/[^A-Za-z0-9]/.test(password)) {
    return 'Password must contain at least one special character';
  }
  return null;
}

/** Convenience predicate: true when `password` satisfies every policy rule. */
export function isPasswordValid(password: string): boolean {
  return getPasswordPolicyError(password) === null;
}
